import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import axios from "axios";
import Spinner from "../../Chat/components/Spinner";
import { FaRegComment } from "react-icons/fa";
import { FiX, FiRefreshCw, FiPlay, FiClock } from "react-icons/fi";
import { MdOutlineAdd } from "react-icons/md";

const WordList = ({ wordList, initialUser, onWordAdded, onWordDeleted }) => {
  const [words, setWords] = useState(wordList.words || []);
  const [newWord, setNewWord] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(initialUser);
  const [expandedSentences, setExpandedSentences] = useState({});
  const [error, setError] = useState(null);
  const [regeneratingWordIds, setRegeneratingWordIds] = useState({});

  useEffect(() => {
    setUser(initialUser);
  }, [initialUser]);

  const addWord = async (e) => {
    e.preventDefault();
    if (!newWord.trim() || isLoading || user.wordsRemaining <= 0) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/v1/word_lists/${wordList.id}/words`,
        { word: newWord },
      );
      const addedWordData = response.data;
      const updatedUser = response.data.user;

      setNewWord("");
      setUser(updatedUser);

      setWords((prevWords) => {
        const newWord = {
          ...addedWordData.word,
          allowRegenerate: true,
        };
        const prevWordsWithoutRegenerate = prevWords.map((word) => ({
          allowRegenerate: false,
          ...word,
        }));
        return [newWord, ...prevWordsWithoutRegenerate];
      });

      //
    } catch (error) {
      console.error("Error adding word:", error);

      if (error.response && error.response.data) {
        if (error.response.data.error === "Language validation failed") {
          setError(
            <div>
              <p className="font-semibold mb-1">Language validation failed:</p>
              <p>{error.response.data.message}</p>
            </div>,
          );
        } else {
          setError("Failed to add word. Please try again.");
        }
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const regenerateSentence = async (wordId) => {
    setRegeneratingWordIds((prev) => ({ ...prev, [wordId]: true }));

    try {
      const response = await axios.post(
        `/api/v1/word_lists/${wordList.id}/words/${wordId}/regenerate_sentence`,
      );

      const updatedWordData = response.data;

      setWords((prevWords) =>
        prevWords.map((word) =>
          word.id === wordId
            ? { ...updatedWordData.word, allowRegenerate: true }
            : word,
        ),
      );
    } catch (error) {
      console.error("Error regenerating sentence:", error);
    } finally {
      setRegeneratingWordIds((prev) => ({ ...prev, [wordId]: false }));
    }
  };

  const deleteWord = async (wordId) => {
    try {
      await axios.delete(`/api/v1/word_lists/${wordList.id}/words/${wordId}`);

      setWords(words.filter((word) => word.id !== wordId));

      if (onWordDeleted) {
        onWordDeleted(wordId);
      }
    } catch (error) {
      console.error("Error deleting word:", error);
      // TODO: Add error handling UI
    }
  };

  const playAudio = (audioUrl, rate = 1) => {
    const audio = new Audio(audioUrl);
    audio.playbackRate = rate;
    audio.play();
  };

  const toggleSentence = (wordId) => {
    setExpandedSentences((prev) => ({
      ...prev,
      [wordId]: !prev[wordId],
    }));
  };

  const truncateSentence = (sentence, wordId) => {
    if (!sentence) return "";
    if (expandedSentences[wordId]) return sentence;

    const maxLength = 60;
    if (sentence.length <= maxLength) return sentence;
    return sentence.substring(0, maxLength) + "...";
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-100 p-4 rounded-lg">
        <p className="text-lg font-semibold">
          Language:{" "}
          <span className="text-blue-600">{wordList.languageName}</span>
        </p>
      </div>

      {user.wordsRemaining > 0 ? (
        <form onSubmit={addWord} className="flex flex-col space-y-2">
          <div className="flex space-x-2">
            <input
              type="text"
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              placeholder={`Enter a new ${wordList.languageName} word`}
              className="flex-grow px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
            />
            <button
              type="submit"
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? <Spinner /> : "Add Word"}
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
        </form>
      ) : (
        <p className="text-red-500">
          You have reached the maximum number of words for this week. Please
          upgrade to add more or wait until next week.
        </p>
      )}

      {user.freemium && (
        <p className="text-sm text-gray-600">
          Words remaining: {user.wordsRemaining}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {words.map((word) => (
          <div
            key={word.id}
            className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition duration-200 flex flex-col h-full"
          >
            <div className="p-4 flex-grow">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center">
                  <div className="bg-blue-100 p-2 rounded-full mr-3">
                    <FaRegComment className="h-5 w-5 text-blue-500" />
                  </div>
                  <h3 className="font-semibold text-lg text-gray-800">
                    {word.word_text}
                  </h3>
                </div>
                <button
                  onClick={() => deleteWord(word.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  aria-label="Delete word"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>

              {word.sentence && (
                <div className="mt-2 text-gray-600 text-sm">
                  <p className="italic">
                    {truncateSentence(word.sentence, word.id)}
                  </p>
                  {word.sentence.length > 60 && (
                    <button
                      onClick={() => toggleSentence(word.id)}
                      className="text-blue-500 hover:text-blue-700 text-xs mt-1"
                    >
                      {expandedSentences[word.id] ? "Show less" : "Read more"}
                    </button>
                  )}
                  {word.allowRegenerate && (
                    <div className="mt-1">
                      <button
                        onClick={() => regenerateSentence(word.id)}
                        className="text-blue-600 hover:text-blue-800  text-xs flex items-center"
                        disabled={regeneratingWordIds[word.id]}
                      >
                        {regeneratingWordIds[word.id] ? (
                          <>
                            <span className="inline-block h-3 w-3 mr-1">
                              <Spinner />
                            </span>
                            Regenerating...
                          </>
                        ) : (
                          <>
                            <FiRefreshCw className="h-3 w-3 mr-1" />
                            Regenerate sentence
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-gray-50 p-3 border-t border-gray-100 mt-auto">
              <div className="flex justify-between">
                {word.audioWordUrl && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => playAudio(word.audioWordUrl)}
                      className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <FiPlay className="h-5 w-5 mr-1" />
                      Play Word
                    </button>
                    <button
                      onClick={() => playAudio(word.audioWordUrl, 0.75)}
                      className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <FiClock className="h-5 w-5 mr-1" />
                      Slow
                    </button>
                  </div>
                )}

                {word.audioSentenceUrl && (
                  <button
                    onClick={() => playAudio(word.audioSentenceUrl)}
                    className="flex items-center text-gray-600 hover:text-gray-800 text-sm"
                  >
                    <FiPlay className="h-5 w-5 mr-1" />
                    Sentence
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {words.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <MdOutlineAdd className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500">
            No words added yet. Add your first word above!
          </p>
        </div>
      )}
    </div>
  );
};

WordList.propTypes = {
  wordList: PropTypes.shape({
    id: PropTypes.number.isRequired,
    languageName: PropTypes.string.isRequired,
    words: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        word_text: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  initialUser: PropTypes.shape({
    wordsRemaining: PropTypes.number.isRequired,
    freemium: PropTypes.bool.isRequired,
  }).isRequired,
  onWordAdded: PropTypes.func,
  onWordDeleted: PropTypes.func,
};

export default WordList;
